import { useCallback, useState } from "react";
import { useChatContext } from "../contexts/ChatContext";
import { useError } from "../contexts/ErrorContext";
import { errorToString } from "../types/typeGuards";
import { Message } from "../types/types";
import log from "../utils/logger";
import useFetchWithMsal from "./useFetchWithMsal";

function looksLike404(error: unknown): boolean {
  const msg =
    typeof error === "object" && error && "message" in error
      ? String((error as any).message)
      : String(error ?? "");
  return /status:\s*404\b/i.test(msg) || /\b404\b/.test(msg);
}

function fallbackFeedbackEndpoint(endpoint: string): string | null {
  const m = endpoint.match(/^(.*)\/[^/]+\/session_manager\/feedback_update\/?$/);
  if (!m) return null;
  return `${m[1]}/session_manager/feedback_update`;
}

export const useFeedback = () => {
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(false);
  const { feedbackEndpoint, fetchMessages } = useChatContext();
  const { execute } = useFetchWithMsal();
  const { addError } = useError();

  const updateFeedback = useCallback(
    async (message: Message, feedback: boolean) => {
      log.debug(`Updating feedback for message ${JSON.stringify(message)}`);
      setFeedbackLoading(true);
      try {
        await execute("POST", feedbackEndpoint, {
          threadId: message.threadId,
          messageId: message.id,
          feedback,
        });
        fetchMessages(message.threadId);
      } catch (error) {
        if (looksLike404(error)) {
          const fallback = fallbackFeedbackEndpoint(feedbackEndpoint);
          if (fallback) {
            try {
              await execute("POST", fallback, {
                threadId: message.threadId,
                messageId: message.id,
                feedback,
              });
              fetchMessages(message.threadId);
              return;
            } catch (e2) {
              // fallback failed -> continue normal error path
              log.error("Fallback feedback_update failed:", e2);
            }
          }
        }
        const errorMessage = errorToString(error);
        log.error("Error posting feedback:", errorMessage);
        addError(`Failed to update feedback: ${errorMessage}`);
      } finally {
        setFeedbackLoading(false);
      }
    },
    [execute, feedbackEndpoint, fetchMessages, addError]
  );

  return { feedbackLoading, updateFeedback };
};
